import React, { useState } from 'react'
import { InventoryItem, ComponentGrade, PartType } from '../types'

const InventoryManagement: React.FC = () => {
  const [inventory] = useState<InventoryItem[]>([
    {
      partId: '1',
      grade: ComponentGrade.NEW,
      quantity: 15,
      totalValue: 389.85,
      components: ['comp-1', 'comp-2', 'comp-3']
    },
    {
      partId: '1',
      grade: ComponentGrade.GRADE_A,
      quantity: 8,
      totalValue: 175.92,
      components: ['comp-4', 'comp-5']
    },
    {
      partId: '2',
      grade: ComponentGrade.GRADE_A,
      quantity: 12,
      totalValue: 540.00,
      components: ['comp-6', 'comp-7', 'comp-8']
    },
    {
      partId: '2',
      grade: ComponentGrade.GRADE_B,
      quantity: 5,
      totalValue: 180.00,
      components: ['comp-9', 'comp-10']
    },
    {
      partId: '3',
      grade: ComponentGrade.GRADE_A,
      quantity: 20,
      totalValue: 1710.00,
      components: ['comp-11', 'comp-12', 'comp-13', 'comp-14']
    }
  ])

  const [parts] = useState([
    { id: '1', sku: 'BAT-001', name: 'Battery Pack', type: PartType.BATTERY, costPrice: 25.99 },
    { id: '2', sku: 'DSP-001', name: 'Display Screen', type: PartType.HEAD, costPrice: 45.00 },
    { id: '3', sku: 'WND-001', name: 'Main Wand', type: PartType.WAND, costPrice: 85.50 }
  ])

  const [selectedGrade, setSelectedGrade] = useState<ComponentGrade | 'all'>('all')
  const [selectedPartType, setSelectedPartType] = useState<PartType | 'all'>('all')

  const getPartInfo = (partId: string) => {
    return parts.find(p => p.id === partId)
  }

  const getGradeColor = (grade: ComponentGrade) => {
    switch (grade) {
      case ComponentGrade.NEW:
      case ComponentGrade.UNUSED:
        return 'status-new'
      case ComponentGrade.GRADE_A:
        return 'status-grade-a'
      case ComponentGrade.GRADE_B:
        return 'status-grade-b'
      case ComponentGrade.GRADE_C:
        return 'status-grade-c'
      case ComponentGrade.SCRAP:
        return 'status-scrap'
      default:
        return 'status-new'
    }
  }

  const filteredInventory = inventory.filter(item => {
    const part = getPartInfo(item.partId)
    if (!part) return false
    
    const gradeMatch = selectedGrade === 'all' || item.grade === selectedGrade
    const typeMatch = selectedPartType === 'all' || part.type === selectedPartType
    
    return gradeMatch && typeMatch
  })

  const totalValue = filteredInventory.reduce((sum, item) => sum + item.totalValue, 0)
  const totalQuantity = filteredInventory.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div>
      <h2>Inventory Management</h2>
      
      <div className="form-section">
        <h3>Inventory Overview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{ 
            background: '#e3f2fd', 
            padding: '1.5rem', 
            borderRadius: '8px', 
            textAlign: 'center' 
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' }}>
              {totalQuantity}
            </div>
            <div style={{ color: '#666' }}>Total Items</div>
          </div>
          <div style={{ 
            background: '#e8f5e8', 
            padding: '1.5rem', 
            borderRadius: '8px', 
            textAlign: 'center' 
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4caf50' }}>
              ${totalValue.toFixed(2)}
            </div>
            <div style={{ color: '#666' }}>Total Value</div>
          </div>
          <div style={{ 
            background: '#fff3e0', 
            padding: '1.5rem', 
            borderRadius: '8px', 
            textAlign: 'center' 
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9800' }}>
              {filteredInventory.length}
            </div>
            <div style={{ color: '#666' }}>SKU Variants</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          <div className="form-group" style={{ minWidth: '200px' }}>
            <label htmlFor="gradeFilter">Filter by Grade</label>
            <select
              id="gradeFilter"
              className="form-control"
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value as ComponentGrade | 'all')}
            >
              <option value="all">All Grades</option>
              <option value={ComponentGrade.NEW}>New/Unused</option>
              <option value={ComponentGrade.GRADE_A}>Grade A</option>
              <option value={ComponentGrade.GRADE_B}>Grade B</option>
              <option value={ComponentGrade.GRADE_C}>Grade C</option>
              <option value={ComponentGrade.SCRAP}>Scrap</option>
            </select>
          </div>

          <div className="form-group" style={{ minWidth: '200px' }}>
            <label htmlFor="typeFilter">Filter by Part Type</label>
            <select
              id="typeFilter"
              className="form-control"
              value={selectedPartType}
              onChange={(e) => setSelectedPartType(e.target.value as PartType | 'all')}
            >
              <option value="all">All Types</option>
              <option value={PartType.WAND}>Wand</option>
              <option value={PartType.HEAD}>Head</option>
              <option value={PartType.BATTERY}>Battery</option>
              <option value={PartType.HOUSING}>Housing</option>
              <option value={PartType.PROCESSOR}>Processor</option>
              <option value={PartType.SENSOR}>Sensor</option>
              <option value={PartType.CABLE}>Cable</option>
              <option value={PartType.OTHER}>Other</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Inventory Details</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Part</th>
              <th>SKU</th>
              <th>Type</th>
              <th>Grade</th>
              <th>Quantity</th>
              <th>Unit Value</th>
              <th>Total Value</th>
              <th>Components</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventory.map((item, index) => {
              const part = getPartInfo(item.partId)
              if (!part) return null

              const unitValue = item.quantity > 0 ? item.totalValue / item.quantity : 0

              return (
                <tr key={`${item.partId}-${item.grade}-${index}`}>
                  <td><strong>{part.name}</strong></td>
                  <td>{part.sku}</td>
                  <td>
                    <span className="status-badge status-new">
                      {part.type.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getGradeColor(item.grade)}`}>
                      {item.grade.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <strong>{item.quantity}</strong>
                  </td>
                  <td>${unitValue.toFixed(2)}</td>
                  <td><strong>${item.totalValue.toFixed(2)}</strong></td>
                  <td>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      {item.components.length} components
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-secondary" style={{ marginRight: '0.5rem' }}>
                      View
                    </button>
                    <button className="btn btn-warning">
                      Adjust
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {filteredInventory.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No inventory items match the selected filters.
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Inventory Movements</h3>
        <div style={{ color: '#666', padding: '1rem', textAlign: 'center' }}>
          Recent inventory movements would be displayed here.
          <br />
          <small>This would show parts added, removed, graded, and transferred.</small>
        </div>
      </div>
    </div>
  )
}

export default InventoryManagement